import { dateStrToAgeDays, dateStrToAgeMonths, Enum_Translation_Locale, PatientDobFormat, useGenerateDescriptiveReportMutation } from '@cyren/common-lib';
import classNames from 'classnames';
import { first } from 'lodash';
import { memo, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useStaticLocales } from '../../admin/hooks/locales/use-static-locale';
import { useToastMsgs } from '../../admin/hooks/use-toast-msgs';
import { useData } from '../../report/use-data';
import { genStepDescKey, genStepNameKey } from '../../utils/models/trees';
import { PaReportState, useQsVisibilitySync } from '../hooks/use-report';
import { useTranslations } from '../hooks/use-translations';
import { useTreeHelpers } from '../hooks/use-tree-helpers';
import { ModeT, TOptions, UpdateAnswerHandler, UpdateAnswerValueHandler } from '../pa-prop-types';
import { Key, Keys, TreeState, UIMetadataT } from '../patient-types';
import PaLocalStorageReset from './PaLocalStorageReset';
import PaTreeHeader from './PaTreeHeader';
import Question from './Question';
import useConfirmationDialog from '../../hooks/use-confirmation-dialog';
import ContentError from '../../common/content-error/content-error';
import { useRecoilState } from 'recoil';
import { useSurveyState } from '../hooks/use-survey-state';
import { AthenaServices } from '../../lib/athena/athena-services';
import { THIRD_PARTY_QUESTION_KEY, THIRD_PARTY_OPTION_SOMEONE_ELSE } from '../../constants';
import produce from 'immer';
import PatientOrgContainer from '../hooks/PatientOrgContainer';

function PaQuestionTree({
  level,
  treeKey,
  onSave,
  hasNext,
  onNext,
  tOptions,
  updateAnswer,
  updateValue,
  onUiMetadataChange,
  treeState,
  locale,
  noHeader,
  mode,
  patientDetails
}: {
  level: number;
  treeKey: string;
  hasNext?: boolean;
  onNext?: () => void;
  onSave?: () => void;
  tOptions?: TOptions;
  treeState?: TreeState;
  updateAnswer: UpdateAnswerHandler;
  updateValue: UpdateAnswerValueHandler;
  locale: Enum_Translation_Locale;
  onUiMetadataChange?: (p: { uiMetadata: UIMetadataT; questionKey: Key }) => void;
  noHeader?: boolean;
  mode?: ModeT;
  patientDetails?: any;
}) {
  const [
    {
      dataState: { survey, visit },
    },
    { getTreeByKey, getQuestionsByKeys },
  ] = useData();

  const [reportState, setReportState] = useRecoilState(PaReportState);

  // generateDescriptiveReport mutation hook (컴포넌트 내부에서 호출)
  const [generateDescriptiveReport, { data: reportData, loading: loadingReport, error: reportError }] = useGenerateDescriptiveReportMutation({
    onError: (error: any) => {
      console.error('Failed to generate descriptive report:', error);
    },
  });

  // make all system values usable as macro replacements
  const extendedTOptions = {
    ...tOptions,
    ...(reportState?.systemValueMap ? Object.fromEntries(
      Object.entries(reportState?.systemValueMap).filter(([, value]) => typeof value === 'string'))
      : {})
  } as TOptions;

  const [, { t }] = useTranslations({ locale });
  const { sltStr } = useStaticLocales();
  const { showAnswerAll } = useToastMsgs();
  const [confirmDialog, confirmationDialogUX] = useConfirmationDialog();
  const container = PatientOrgContainer.useContainer();

  const [{ isValid, qsKeysNotAnswered, qsErrorMap }] = useQsVisibilitySync({
    treeState,
  });

  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (treeKey) {
      setDirty(false);
    }
  }, [treeKey]);

  const [errorQsKeys, setErrorQsKeys] = useState<Keys>([]);
  const tree = treeKey ? getTreeByKey(treeKey) : null;
  const questions = tree ? getQuestionsByKeys(tree.entryQuestionKeys) : null;
  const [, { isQuestionVisible }] = useTreeHelpers({ treeState });

  // Wrapper for updateAnswer to handle third-party question
  const wrappedUpdateAnswer: UpdateAnswerHandler = (params) => {
    const { questionKey, answerKeys } = params;

    // Handle the auto-generated third-party question
    if (questionKey === THIRD_PARTY_QUESTION_KEY && answerKeys) {
      const isThirdParty = answerKeys.includes(THIRD_PARTY_OPTION_SOMEONE_ELSE);
      setReportState((st) =>
        produce(st, (dr) => {
          dr.isThirdParty = isThirdParty;
        })
      );
    }

    // Call the original updateAnswer
    updateAnswer(params);
  };

  // surveyState 사용 (early return 전에 호출)
  const [{ surveyState, visit: visitFromSurveyState }, { setSurveyState }] = useSurveyState();

  // eslint-disable-next-line
  // treeKey
  // tree
  // questions
  // treeState

  if (!treeState) {
    // TODO handle null
    return null;
  }

  // tree
  // treeIdx
  // survey

  const nameKey = genStepNameKey(tree);
  const descKey = genStepDescKey(tree);

  const treeLabel = t(nameKey || '', {}, { fallbackValue: '' });
  const desc = t(descKey || '', {}, { fallbackValue: '' });
  const showHeader = !tree?.stageBoundary;

  // 설문 데이터에서 환자 정보 추출하는 헬퍼 함수
  const extractPatientInfoFromSurvey = (systemValueMap: any) => {
    if (!systemValueMap) return null;

    // 설문에서 환자 정보를 추출하는 로직
    // 다양한 필드명 패턴을 시도 (기본값 제거 - 실제 데이터만 사용)
    const patientInfo = {
      firstname: systemValueMap.firstName || systemValueMap.first_name || systemValueMap.firstname || systemValueMap.name?.split(' ')[0] || '',
      lastname: systemValueMap.lastName || systemValueMap.last_name || systemValueMap.lastname || systemValueMap.name?.split(' ')[1] || '',
      // age 필드도 DOB로 사용될 수 있음
      dob: systemValueMap.dateOfBirth || systemValueMap.dob || systemValueMap.birthDate || systemValueMap.birthday || systemValueMap.age || '',
      departmentid: systemValueMap.departmentId || systemValueMap.department_id || systemValueMap.departmentid || '',
      mobilephone: systemValueMap.mobilePhone || systemValueMap.phone || systemValueMap.mobile || systemValueMap.telephone || '',
      homephone: systemValueMap.homePhone || systemValueMap.homephone || '',
      workphone: systemValueMap.workPhone || systemValueMap.workphone || '',
    };

    return patientInfo;
  };

  const handleMedication = async () => {
    // 기존 CCDA 불러오기
    const patientId = surveyState?.patientDetails?.patientid;
    const departmentId = surveyState?.patientDetails?.departmentid;
    const allergyFinal = surveyState?.allergyFinal;

    // CCDA를 한 번만 불러와서 공유
    // let ccdaXml = await AthenaServices.getCCDAExport(patientId, departmentId, encounterId ?? '');
    let ccdaXml = await AthenaServices.getCCDA(patientId, departmentId);
    ccdaXml = ccdaXml.data[0].ccda;

    if (allergyFinal && allergyFinal.length > 0) {
      const reactionMappingObject = {
        "sx-angioedema" : "angioedema",
        "sx-urticaria": "hives",
        "sx-anaphylaxis": "anaphylaxis",
        "sx-abd-pain" : "abdominal pain",
        "sx-diarrhea" : "diarrhea",
        "sx-irregular-heartbeat" : "irregular heart rate",
        "sx-palpitation": "palpitations",
        "sx-confusion": "confusion",
        "sx-dizziness": "dizziness",
        "sx-seizure": "seizure",
        "sx-eye-red": "eye redness",
        "o-stevens-johnson": "stevens-johnson syndrome",
        "o-joint-pain": "arthralgia",
        "o-other": "other",
      }

      // AthenaServices.getReaction()은 항상 전체 반응 리스트를 반환하므로, 별도의 API 호출 없이 reaction 리스트를 상수로 정의하여 사용
      const reactionList = [
        { snomedcode: 21522001, reactionname: "Abdominal pain", reactionid: 129 },
        { snomedcode: 274647009, reactionname: "Agitation", reactionid: 370 },
        { snomedcode: 39579001, reactionname: "Anaphylaxis", reactionid: 2 },
        { snomedcode: 41291007, reactionname: "Angioedema", reactionid: 49 },
        { snomedcode: 48694002, reactionname: "Anxiety", reactionid: 369 },
        { snomedcode: 57676002, reactionname: "Arthralgia (joint pain)", reactionid: 69 },
        { snomedcode: 48867003, reactionname: "Bradycardia", reactionid: 149 },
        { snomedcode: 410429000, reactionname: "Cardiac arrest", reactionid: 349 },
        { snomedcode: 29857009, reactionname: "Chest pain", reactionid: 9 },
        { snomedcode: 40917007, reactionname: "Confusion", reactionid: 130 },
        { snomedcode: 49727002, reactionname: "Cough", reactionid: 8 },
        { snomedcode: 12763006, reactionname: "Decreased blood pressure", reactionid: 135 },
        { snomedcode: 62315008, reactionname: "Diarrhea", reactionid: 11 },
        { snomedcode: 404640003, reactionname: "Dizziness", reactionid: 16 },
        { snomedcode: 87715008, reactionname: "Dry mouth", reactionid: 249 },
        { snomedcode: 267036007, reactionname: "Dyspnea", reactionid: 329 },
        { snomedcode: 267038008, reactionname: "Edema", reactionid: 109 },
        { snomedcode: 45170000, reactionname: "Encephalitis", reactionid: 229 },
        { snomedcode: 75705005, reactionname: "Eye redness", reactionid: 169 },
        { snomedcode: 45177002, reactionname: "Eye swelling", reactionid: 131 },
        { snomedcode: 278528006, reactionname: "Facial swelling", reactionid: 132 },
        { snomedcode: 386661006, reactionname: "Fever", reactionid: 133 },
        { snomedcode: 403618004, reactionname: "Flushing", reactionid: 134 },
        { snomedcode: 74474003, reactionname: "GI bleed", reactionid: 151 },
        { snomedcode: 278040002, reactionname: "Hair loss", reactionid: 269 },
        { snomedcode: 7011001, reactionname: "Hallucinations", reactionid: 141 },
        { snomedcode: 25064002, reactionname: "Headache", reactionid: 17 },
        { snomedcode: 126485001, reactionname: "Hives", reactionid: 7 },
        { snomedcode: 193462001, reactionname: "Insomnia", reactionid: 190 },
        { snomedcode: 361138002, reactionname: "Irregular heart rate", reactionid: 10 },
        { snomedcode: 418290006, reactionname: "Itching", reactionid: 3 },
        { snomedcode: 386705008, reactionname: "Lightheadedness", reactionid: 136 },
        { snomedcode: 48079002, reactionname: "Mood change/worsened mood", reactionid: 371 },
        { snomedcode: 55300003, reactionname: "Muscle cramps", reactionid: 309 },
        { snomedcode: 68962001, reactionname: "Myalgias (muscle pain)", reactionid: 29 },
        { snomedcode: 422587007, reactionname: "Nausea", reactionid: 14 },
        { snomedcode: 74964007, reactionname: "Other", reactionid: 89 },
        { snomedcode: 80313002, reactionname: "Palpitations", reactionid: 209 },
        { snomedcode: 90128006, reactionname: "Photosensitivity", reactionid: 12 },
        { snomedcode: 271807003, reactionname: "Rash", reactionid: 1 },
        { snomedcode: 271825005, reactionname: "Respiratory distress", reactionid: 13 },
        { snomedcode: 60862001, reactionname: "Ringing in ears", reactionid: 138 },
        { snomedcode: 91175000, reactionname: "Seizure", reactionid: 189 },
        { snomedcode: 73442001, reactionname: "Stevens-Johnson syndrome", reactionid: 137 },
        { snomedcode: 6471006, reactionname: "Suicidal thoughts", reactionid: 373 },
        { snomedcode: 65124004, reactionname: "Swelling", reactionid: 289 },
        { snomedcode: 3424008, reactionname: "Tachycardia", reactionid: 150 },
        { snomedcode: 31996006, reactionname: "Vasculitis", reactionid: 139 },
        { snomedcode: 422400008, reactionname: "Vomiting", reactionid: 15 },
        { snomedcode: 56018004, reactionname: "Wheezing", reactionid: 140 },
        { snomedcode: 91019004, reactionname: "numbness/tingling/paresthesia", reactionid: 372 }
      ];

      // 모든 알러지에 대한 데이터를 병렬로 처리
      const allergyPromises = allergyFinal.map(async (allergy: any, index: number) => {
        const allergyResult = await AthenaServices.getAllergy(allergy.allergy);
        
        // allergyResult.data.allergies 배열에서 입력값과 가장 유사한 allergen을 찾는다.
        let selectedAllergy = null;
        if (allergyResult?.data?.length > 0) {
          // 완전 일치 우선, 없으면 소문자 비교
          selectedAllergy = allergyResult.data.find(
            (item: any) => item.allergenname === allergy.allergy
          ) || allergyResult.data.find(
            (item: any) => item.allergenname.toLowerCase() === allergy.allergy.toLowerCase()
          ) || allergyResult.data[0]; // 그래도 없으면 첫 번째 값 사용
        }

        // reactions 처리
        const reactions = allergy.reactions || [];
        const reactionRows = reactions.map((reaction: any, reactionIndex: number) => {
          const reactionName = reactionMappingObject[reaction as keyof typeof reactionMappingObject] ?? "Other";
          const allergyReaction = reactionList.find((item: any) => item.reactionname.toLowerCase().includes(reactionName)) ?? { snomedcode: 74964007, reactionname: "Other", reactionid: 89 };
          
          return `
            <tr>
              <td/>
              <td/>
              <td><content ID="allergy${index + 1}allergen">${selectedAllergy?.allergenname || allergy.allergy}</content></td>
              <td><content ID="allergy${index + 1}reaction${reactionIndex + 1}">${allergyReaction.reactionname}</content></td>
              <td><content ID="allergy${index + 1}severity${reactionIndex + 1}">&#xA0;</content></td>
              <td>Active</td>
              <td><content ID="allergy${index + 1}onset">&#xA0;</content></td>
            </tr>
          `;
        }).join('');

        return {
          selectedAllergy,
          reactionRows,
          reactions
        };
      });

      const allergyData = await Promise.all(allergyPromises);

      // 모든 알러지를 포함한 단일 테이블 생성
      const allReactionRows = allergyData.map((data, index) => data.reactionRows).join('');

      // CCDA XML에서 allergy section 생성
      const allergyCCDASection = `
        <section>
          <templateId root="2.16.840.1.113883.10.20.22.2.6.1"/>
          <id root="db83ee7a-902d-11f0-882c-c114a2c7d8a0"/>
          <code code="48765-2" codeSystem="2.16.840.1.113883.6.1" codeSystemName="LOINC"/>
          <title>Allergies</title>
          <text>
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Code System</th>
                  <th>Name</th>
                  <th>Reaction</th>
                  <th>Severity</th>
                  <th>Status</th>
                  <th>Onset</th>
                </tr>
              </thead>
              <tbody>
                ${allReactionRows}
              </tbody>
            </table>
          </text>
          <entry>
            <act classCode="ACT" moodCode="EVN">
              <templateId root="2.16.840.1.113883.10.20.22.4.30"/>
              <id root="db8418a0-902d-11f0-ba75-9a7168811237"/>
              <code code="48765-2" codeSystem="2.16.840.1.113883.6.1" codeSystemName="LOINC" displayName="Allergies, adverse reactions, alerts"/>
              <statusCode code="active"/>
              <effectiveTime>
                <low value="${new Date().toISOString().replace(/[-:]/g, '').replace(/\..+/, '')}-0400"/>
              </effectiveTime>
              ${allergyData.map((data, index) => `
                <entryRelationship typeCode="SUBJ">
                  <observation classCode="OBS" moodCode="EVN">
                    <templateId root="2.16.840.1.113883.10.20.22.4.7"/>
                    <id root="db844d5c-902d-11f0-a840-e98c0e6605c3"/>
                    <code code="ASSERTION" codeSystem="2.16.840.1.113883.5.4"/>
                    <statusCode code="completed"/>
                    <effectiveTime>
                      <low nullFlavor="NI"/>
                    </effectiveTime>
                    <value xsi:type="CD" code="419199007" codeSystem="2.16.840.1.113883.6.96" displayName="Allergy to substance"/>
                    <participant typeCode="CSM">
                      <participantRole classCode="MANU">
                        <playingEntity classCode="MMAT">
                          <code nullFlavor="NI">
                            <originalText>
                              <reference value="#allergy${index + 1}allergen"/>
                            </originalText>
                            <translation code="${data.selectedAllergy?.allergenid || 0}" codeSystemName="ATHENA_ALLERGYID" displayName="${data.selectedAllergy?.allergenname || allergyFinal[index].allergy}"/>
                          </code>
                        </playingEntity>
                      </participantRole>
                    </participant>
                    ${data.reactions.map((reaction: any, reactionIndex: number) => {
                      const reactionName = reactionMappingObject[reaction as keyof typeof reactionMappingObject] ?? "Other";
                      const allergyReaction = reactionList.find((item: any) => item.reactionname.toLowerCase().includes(reactionName)) ?? { snomedcode: 74964007, reactionname: "Other", reactionid: 89 };
                      
                      return `
                        <entryRelationship typeCode="MFST" inversionInd="true">
                          <observation classCode="OBS" moodCode="EVN">
                            <templateId root="2.16.840.1.113883.10.20.22.4.9"/>
                            <id root="db847d22-902d-11f0-adce-af4920907b1b"/>
                            <code nullFlavor="NI"/>
                            <text>
                              <reference value="#allergy${index + 1}reaction${reactionIndex + 1}"/>
                            </text>
                            <statusCode code="completed"/>
                            <value code="${allergyReaction.snomedcode}" codeSystem="2.16.840.1.113883.6.96" displayName="${allergyReaction.reactionname}" xsi:type="CD"/>
                          </observation>
                        </entryRelationship>
                      `;
                    }).join('')}
                    <entryRelationship typeCode="SUBJ" inversionInd="true">
                      <observation moodCode="EVN" classCode="OBS">
                        <templateId root="2.16.840.1.113883.10.20.22.4.8"/>
                        <code code="SEV" codeSystem="2.16.840.1.113883.5.4" codeSystemName="HL7ActCode" displayName="Severity Observation"/>
                        <text>
                          <reference value="#allergy${index + 1}severity1"/>
                        </text>
                        <statusCode code="completed"/>
                        <value xsi:type="CD" nullFlavor="NI"/>
                      </observation>
                    </entryRelationship>
                  </observation>
                </entryRelationship>
              `).join('')}
            </act>
          </entry>
        </section>
      `;

      // 기존 allergy section을 찾아서 교체
      const allergySectionRegex = /<section[^>]*>[\s\S]*?<code code="48765-2"[\s\S]*?<\/section>/;
      
      if (allergySectionRegex.test(ccdaXml)) {
        // 기존 allergy section이 있으면 교체
        ccdaXml = ccdaXml.replace(allergySectionRegex, allergyCCDASection);
      } else {
        // 기존 allergy section이 없으면 새로 추가
        // structuredBody 안의 첫 번째 component 다음에 추가
        const structuredBodyRegex = /(<structuredBody>[\s\S]*?<component>[\s\S]*?<\/component>)/;
        if (structuredBodyRegex.test(ccdaXml)) {
          ccdaXml = ccdaXml.replace(structuredBodyRegex, `$1\n\t\t\t<component>\n\t\t\t\t${allergyCCDASection}\n\t\t\t</component>`);
        } else {
          console.error('structuredBody를 찾을 수 없습니다.');
        }
      }
    }

    // medication과 allergy 모두 처리한 후 한 번에 업데이트
    if (allergyFinal) {
      await AthenaServices.updateCCDA(patientId, ccdaXml, departmentId);
    }
  };

  const sendDescriptiveNoteToAthena = async () => {
    const encounter = await AthenaServices.getEncounter(surveyState?.patientDetails?.patientid, surveyState?.patientDetails?.departmentid);
    const encounterId = encounter.data.encounters[0].encounterid;

    // 필요한 데이터 추출
    const cc = reportState?.systemValueMap?.cc || '';
    const treeStatesInfo = reportState?.treeStates || [];
    
    // 디버깅을 위해 treeStates 구조 확인
    
    // HPI 데이터 추출 - treeStates에서 answerMap을 통해 데이터 수집
    const hpiData: any[] = [];
    const allergyData: any[] = [];
    const pmhData: any[] = [];
    const medicationData: any[] = [];
    
    // 의료 줄임말을 표준 의학 용어로 변환하는 사전
    const medicalAbbreviations: { [key: string]: string } = {
      // 신체 부위
      'Abd': 'abdomen',
      'Abd Whole': 'whole abdomen',
      'Chest': 'chest',
      'Chest Rt Upper': 'right upper chest',
      'Chest Lt Upper': 'left upper chest',
      'Chest Rt Lower': 'right lower chest',
      'Chest Lt Lower': 'left lower chest',
      'Rt': 'right',
      'Lt': 'left',
      'Upper': 'upper',
      'Lower': 'lower',
      
      // 증상 관련
      'Abd Pain': 'abdominal pain',
      'Chest Pain': 'chest pain',
      'Groin Pain': 'groin pain',
      'Head Pain': 'headache',
      'Back Pain': 'back pain',
      
      // 시간 관련
      'Onset': 'onset',
      'Duration': 'duration',
      'Frequency': 'frequency',
      'Nature': 'nature',
      'Context': 'context',
      'Progression': 'progression',
      
      // 기타
      'PMH': 'past medical history',
      'PMHx': 'past medical history',
      'CC': 'chief complaint',
      'HPI': 'history of present illness'
    };
    
    // 의료 줄임말을 표준 용어로 변환하는 함수
    const convertMedicalAbbreviation = (text: string): string => {
      // 정확한 매치가 있으면 변환
      if (medicalAbbreviations[text]) {
        return medicalAbbreviations[text];
      }
      
      // 부분 매치가 있는지 확인 (예: "Abd Pain" → "abdominal pain")
      const found = Object.entries(medicalAbbreviations).find(([abbrev]) => text.includes(abbrev));
      if (found) {
        const [abbrev, full] = found;
        return text.replace(abbrev, full);
      }
      
      // 매치가 없으면 원본 반환
      return text;
    };
    
    treeStatesInfo.forEach((currentTreeState: any, index: number) => {
      
      if (currentTreeState?.answerMap) {
        Object.entries(currentTreeState.answerMap).forEach(([questionKey, answer]: [string, any]) => {
          
          // answerValues가 있는 경우도 처리
          if (answer?.answerValues && answer.answerValues.length > 0) {
            const value = answer.answerValues[0];
            if (value && typeof value === 'string' && value.trim()) {
              const currentTreeKey = currentTreeState.treeKey || 'unknown';
              
              // currentTreeKey에 따라 분류하고 더 적절한 라벨 사용
              if (currentTreeKey.toLowerCase().includes('allergy')) {
                // 알레르기 정보는 더 간단하게
                allergyData.push(`Allergy: ${value}`);
              } else if (currentTreeKey.toLowerCase().includes('pmh') || 
                         currentTreeKey.toLowerCase().includes('history')) {
                // PMH 정보도 더 간단하게
                pmhData.push(`PMH: ${value}`);
              } else if (currentTreeKey.toLowerCase().includes('medication') ||
                         currentTreeKey.toLowerCase().includes('med')) {
                // 약물 정보는 medicationData에 추가
                medicationData.push(`Medication: ${value}`);
              } else if (currentTreeKey.toLowerCase().includes('injury') ||
                         currentTreeKey.toLowerCase().includes('pain') ||
                         currentTreeKey.toLowerCase().includes('symptom')) {
                // 부상, 통증, 증상 관련 데이터도 HPI에 추가
                hpiData.push(`${currentTreeKey}: ${value}`);
              }
            }
          }
          
          // answerKeys가 있는 경우도 처리 (선택된 옵션들)
          if (answer?.answerKeys && answer.answerKeys.length > 0) {
            answer.answerKeys.forEach((key: string) => {
              // notesForKeys에서 해당 키의 노트 가져오기
              const noteForKey = answer?.notesForKeys?.[key];
              const hasNote = noteForKey && typeof noteForKey === 'string' && noteForKey.trim();
              
              if (key.startsWith('o-')) {
                const optionText = key.replace('o-', '').replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
                const currentTreeKey = currentTreeState.treeKey || 'unknown';
                
                // currentTreeKey에 따라 분류
                if (currentTreeKey.toLowerCase().includes('pmh') || 
                    currentTreeKey.toLowerCase().includes('history')) {
                  // PMH 관련 answerKeys도 추가
                  const pmhText = hasNote 
                    ? `PMH - ${questionKey.replace('q-', '').replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}: ${optionText} (${noteForKey})`
                    : `PMH - ${questionKey.replace('q-', '').replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}: ${optionText}`;
                  pmhData.push(pmhText);
                } else if (currentTreeKey.toLowerCase().includes('medication') ||
                           currentTreeKey.toLowerCase().includes('med')) {
                  // 약물 관련 answerKeys도 추가
                  const medText = hasNote
                    ? `Medication - ${questionKey.replace('q-', '').replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}: ${optionText} (${noteForKey})`
                    : `Medication - ${questionKey.replace('q-', '').replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}: ${optionText}`;
                  medicationData.push(medText);
                } else {
                  // HPI에 추가 (증상, 부상 등)
                  const hpiText = hasNote
                    ? `${questionKey.replace('q-', '').replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}: ${optionText} (${noteForKey})`
                    : `${questionKey.replace('q-', '').replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}: ${optionText}`;
                  hpiData.push(hpiText);
                }
              }

              if (key.startsWith('sx-')) {
                const optionText = key.replace('sx-', '').replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
                const currentTreeKey = currentTreeState.treeKey || 'unknown';
                
                // currentTreeKey에 따라 분류
                if (currentTreeKey.toLowerCase().includes('allergy')) {
                  // 알레르기 관련 answerKeys도 추가
                  const allergyText = hasNote
                    ? `Allergy Reaction - ${questionKey.replace('q-', '').replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}: ${optionText} (${noteForKey})`
                    : `Allergy Reaction - ${questionKey.replace('q-', '').replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}: ${optionText}`;
                  allergyData.push(allergyText);
                } else {
                  // HPI에 추가 (증상 관련)
                  const hpiText = hasNote
                    ? `${questionKey.replace('q-', '').replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}: ${optionText} (${noteForKey})`
                    : `${questionKey.replace('q-', '').replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}: ${optionText}`;
                  hpiData.push(hpiText);
                }
              }
            });
          }
        });
      } 
      
      // PMH 데이터 특별 처리 (pmhx-screen treeKey)
      if (currentTreeState.treeKey === 'pmhx-screen' && currentTreeState.answerMap) {
        Object.entries(currentTreeState.answerMap).forEach(([questionKey, answer]: [string, any]) => {
          // 질문 키를 사람이 읽기 쉬운 형태로 변환
          let questionText = questionKey.replace('q-', '').replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
          
          // 특정 질문들을 더 명확하게 변환
          if (questionKey === 'q-relevant-history') questionText = 'Relevant History';
          else if (questionKey.includes('q-medication-pta-time')) questionText = 'Medication Prior to Admission Time';
          else if (questionKey.includes('q-medication-pta')) questionText = 'Medication Prior to Admission';
          
          // answerValues가 있는 경우
          if (answer?.answerValues && answer.answerValues.length > 0) {
            const value = answer.answerValues[0];
            if (value && typeof value === 'string' && value.trim()) {
              pmhData.push(`PMH - ${questionText}: ${value}`);
            }
          }
          
          // answerKeys가 있는 경우 (선택된 옵션들)
          if (answer?.answerKeys && answer.answerKeys.length > 0) {
            answer.answerKeys.forEach((key: string) => {
              if (key.startsWith('o-')) {
                const optionText = key.replace('o-', '').replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
                pmhData.push(`PMH - ${questionText}: ${optionText}`);
              }
            });
          }
        });
      }

      // 증상별 세부 데이터 수집 (sx-chestpain, sx-myalgia 등)
      if (currentTreeState.treeKey && currentTreeState.treeKey.startsWith('sx-')) {
        const symptomKey = currentTreeState.treeKey;
        const symptomName = symptomKey.replace('sx-', '').replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
        
        if (currentTreeState.answerMap) {
          Object.entries(currentTreeState.answerMap).forEach(([questionKey, answer]: [string, any]) => {
            // answerValues가 있는 경우 해당 값들을 증상과 연결하여 추가
            if (answer?.answerValues && answer.answerValues.length > 0) {
              const value = answer.answerValues[0];
              if (value && typeof value === 'string' && value.trim()) {
                // 질문 키를 사람이 읽기 쉬운 형태로 변환
                let questionText = questionKey.replace('q-', '').replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
                
                // 특정 질문들을 더 명확하게 변환
                if (questionKey === 'q-onset-datetime') questionText = 'Onset';
                else if (questionKey === 'q-sx-count') questionText = 'Frequency';
                else if (questionKey === 'q-sx-duration') questionText = 'Duration';
                else if (questionKey === 'q-sx-continuous-intermittent') questionText = 'Nature';
                else if (questionKey === 'q-chest-location') questionText = 'Location';
                else if (questionKey === 'q-abrupt-gradual') questionText = 'Onset Type';
                else if (questionKey === 'q-chest-event') questionText = 'Context';
                else if (questionKey === 'q-sx-aggravation') questionText = 'Progression';
                
                // 의료 줄임말 변환 적용
                const convertedSymptomName = convertMedicalAbbreviation(symptomName);
                const convertedQuestionText = convertMedicalAbbreviation(questionText);
                const convertedValue = convertMedicalAbbreviation(value);

                // 날짜 포맷: YYYY-MM-DD, YYYY-MM-DD HH:MM, YYYY-MM-DD HH
                // 날짜 변환 유틸리티
                const humanizeDateDifference = (dateStr: string): string => {
                  if (!dateStr) return dateStr;
                  const now = new Date();
                  let date;
                  let diffText = dateStr;

                  // YYYY-MM-DD HH:MM
                  const ymdhmMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})$/);
                  // YYYY-MM-DD HH
                  const ymdhMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2})$/);
                  // YYYY-MM-DD only
                  const ymdMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);

                  if (ymdhmMatch) {
                    // YYYY-MM-DD HH:MM
                    date = new Date(
                      Number(ymdhmMatch[1]), Number(ymdhmMatch[2]) - 1, Number(ymdhmMatch[3]),
                      Number(ymdhmMatch[4]), Number(ymdhmMatch[5])
                    );
                    const diffMs = now.getTime() - date.getTime();
                    if (diffMs < 60000) {
                      diffText = "Just now";
                    } else if (diffMs < 3600000) {
                      const min = Math.floor(diffMs / 60000);
                      diffText = `${min} minute${min === 1 ? '' : 's'} ago`;
                    } else if (diffMs < 86400000) {
                      const hours = Math.floor(diffMs / 3600000);
                      diffText = `${hours} hour${hours === 1 ? '' : 's'} ago`;
                    } else {
                      const days = Math.floor(diffMs / 86400000);
                      diffText = `${days} day${days === 1 ? '' : 's'} ago`;
                    }
                  } else if (ymdhMatch) {
                    // YYYY-MM-DD HH
                    date = new Date(
                      Number(ymdhMatch[1]), Number(ymdhMatch[2]) - 1, Number(ymdhMatch[3]), 
                      Number(ymdhMatch[4]), 0
                    );
                    const diffMs = now.getTime() - date.getTime();
                    if (diffMs < 3600000) {
                      const min = Math.floor(diffMs / 60000);
                      diffText = `${min} minute${min === 1 ? '' : 's'} ago`;
                    } else if (diffMs < 86400000) {
                      const hours = Math.floor(diffMs / 3600000);
                      diffText = `${hours} hour${hours === 1 ? '' : 's'} ago`;
                    } else {
                      const days = Math.floor(diffMs / 86400000);
                      diffText = `${days} day${days === 1 ? '' : 's'} ago`;
                    }
                  } else if (ymdMatch) {
                    // YYYY-MM-DD
                    date = new Date(Number(ymdMatch[1]), Number(ymdMatch[2]) - 1, Number(ymdMatch[3]));
                    const diffMs = now.setHours(0,0,0,0) - date.getTime(); // ignore hours/mins for date-only
                    const days = Math.floor(diffMs / 86400000);
                    if (days < 7) {
                      diffText = `${days} day${days === 1 ? '' : 's'} ago`;
                    } else if (days < 30) {
                      const weeks = Math.floor(days / 7);
                      diffText = `${weeks} week${weeks === 1 ? '' : 's'} ago`;
                    } else if (days < 365) {
                      const months = Math.floor(days / 30);
                      diffText = `${months} month${months === 1 ? '' : 's'} ago`;
                    } else {
                      const years = Math.floor(days / 365);
                      diffText = `${years} year${years === 1 ? '' : 's'} ago`;
                    }
                  } 
                  return diffText;
                }

                let finalConvertedValue = convertedValue;
                // Onset에 대해 날짜 치환 적용
                if (
                  (questionText === 'Onset' || convertedQuestionText === 'Onset' || convertedQuestionText.includes('Datetime')) &&
                  typeof convertedValue === 'string' &&
                  (
                    convertedValue.match(/^\d{4}-\d{2}-\d{2}$/) ||
                    convertedValue.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/) ||
                    convertedValue.match(/^\d{4}-\d{2}-\d{2} \d{2}$/)
                  )
                ) {
                  finalConvertedValue = humanizeDateDifference(convertedValue);
                }

                let finalConvertedQuestionText = convertedQuestionText;
                if (convertedQuestionText.includes('Datetime')) {
                  finalConvertedQuestionText = convertedQuestionText.replace('Datetime', 'Onset');
                }
                
                hpiData.push(`${convertedSymptomName} - ${convertedQuestionText}: ${finalConvertedValue}`);
              }
            }
            
            // answerKeys가 있는 경우도 처리 (선택된 옵션들)
            if (answer?.answerKeys && answer.answerKeys.length > 0) {
              answer.answerKeys.forEach((key: string) => {
                if (key.startsWith('o-')) {
                  const optionText = key.replace('o-', '').replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
                  
                  // 질문 키를 사람이 읽기 쉬운 형태로 변환
                  let questionText = questionKey.replace('q-', '').replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
                  
                  // 특정 질문들을 더 명확하게 변환
                  if (questionKey === 'q-sx-continuous-intermittent') questionText = 'Nature';
                  else if (questionKey === 'q-chest-location') questionText = 'Location';
                  else if (questionKey === 'q-abrupt-gradual') questionText = 'Onset Type';
                  else if (questionKey === 'q-chest-event') questionText = 'Context';
                  else if (questionKey === 'q-sx-aggravation') questionText = 'Progression';
                  
                  // 의료 줄임말 변환 적용
                  const convertedSymptomName = convertMedicalAbbreviation(symptomName);
                  const convertedQuestionText = convertMedicalAbbreviation(questionText);
                  const convertedOptionText = convertMedicalAbbreviation(optionText);
                  
                  hpiData.push(`${convertedSymptomName} - ${convertedQuestionText}: ${convertedOptionText}`);
                }
              });
            }
          });
        }
      }
    });

    // 과거 약(medication 또는 currentMedication)에서 정보 추출 (현재 약과 구분)
    // q-dhx-description-athena는 currentMedication에 저장됨
    const currentMedication = surveyState?.currentMedication;  // => 프롬프트
    
    // currentMedication 필드 확인 (q-dhx-description-athena에서 온 과거 약)
    if (currentMedication && typeof currentMedication === 'string') {
      const pastMedicationsFromCurrent = currentMedication.split('|').filter(med => med.trim());
      pastMedicationsFromCurrent.forEach(med => {
        if (med.trim()) {
          // 이미 추가되지 않았는지 확인 (중복 방지)
          const alreadyAdded = medicationData.some(item => item.includes(med.trim()));
          if (!alreadyAdded) {
            medicationData.push(`Past Medication: ${med.trim()}`);
          }
        }
      });
    }

    // 환자 정보 추출
    const patientInfo = extractPatientInfoFromSurvey(reportState?.systemValueMap);

    // 나이 계산
    let age: number | null = null;
    const dob = patientInfo?.dob || "";
    const monthAge = dateStrToAgeMonths(dob, {
      inputFormat: PatientDobFormat,
    });
    const dayAge = dateStrToAgeDays(dob, {
      inputFormat: PatientDobFormat,
    });

    if (dob) {
      const birthDate = new Date(dob);
      const today = new Date();
      const currentYear = today.getFullYear();
      const birthYear = birthDate.getFullYear();
      age = currentYear - birthYear;
      const monthDifference = today.getMonth() - birthDate.getMonth();
      const dayDifference = today.getDate() - birthDate.getDate();
      if (monthDifference < 0 || (monthDifference === 0 && dayDifference < 0)) {
          age--;
      }
    }

    // 성별 추출
    const gender = reportState?.systemValueMap?.sex?.toString().replace("o-", "") || 'Gender is Not provided';

    // cc, pmh, allergy, hpi, medication 데이터는 위에서 추출한 값 사용
    // promptInfoHpi, treeStatesInfo는 이 context에 없으므로 hpiData, pmhData, allergyData, medicationData로 대체

    const ageString = (() => {
      if (age !== null && age >= 2) {
        return `${age} years old`;
      }
      if (monthAge !== null && monthAge >= 1) {
        return `${monthAge} months old`;
      }
      if (dayAge !== null && monthAge < 1) {
        return `${dayAge} days old`;
      }
      return 'Age is Not provided';
    })();

    const isKorean = container.defaultLanguage === 'ko';

    // descriptive note prompt 생성 (DescriptiveButton.tsx와 동일한 형식)
    const englishPrompt = `
You are an expert medical scribe specializing in clinical documentation. Your task is to transform the following raw, patient-generated clinical data into a formal History of Present Illness (HPI) note.
The HPI must be a clear, concise, and chronologically organized narrative written in professional medical prose. Follow these specific principles:
1. Structure of the HPI:
- Opening Statement: The first sentence is critical. It must introduce the patient by stating their age, gender, any relevant past medical history, and their chief complaint. If Past Medical History is not provided or empty, do not mention it.
Example: "The patient is a 34-year-old male with no relevant past medical history who presents for evaluation following a motor vehicle collision."
However, If the age of the patient is below 2, describe age in months instead of years. In addition, if monthAge is provided, use it instead of age.
- Narrative Body: After the opening, build a chronological story of the events. Integrate the details of the chief complaint using the OLDCARTS framework as a guide to ensure completeness:
Onset: When did it start? (DO NOT USE 'Approximately' in the answer)
Location: Where is the symptom?
Duration: How long has it been going on?
Character: What does the symptom feel like?
Aggravating/Alleviating Factors: What makes it better or worse?
Radiation: Does the pain or symptom travel?
Timing: Is it constant or intermittent?
Severity: How bad is it?
Associated Symptoms & Context: Weave in any related details, such as the mechanism of injury, treatments attempted, and pertinent negatives (the absence of key symptoms, like denying head trauma).
2. MUST WRITE IN THE FOLLOWING ORDER:
(1) Opening Statement (First sentence)
(2) Narrative Body (After the opening, build a chronological story of the events. Integrate the details of the chief complaint using the OLDCARTS framework as a guide to ensure completeness)
(3) If there is a ‘Medication Prior to Admission’, mention it in the last sentence of the narrative body.
(4) Do not write ‘DHx’ in narrative form. If provided, list them at the end as follows, If there is no medication, mention it ‘None’:
Example:
Medication: Lisinopril, Tylenol
(5) Do not write Allergy in narrative form. If provided, list them at the end as follows, If there is no allergy, mention it 'None':
Example:
Allergy: Bacmin, Cat'claw
(6) If there is a language other than English, document it like the following example. Example) The patient is a 44-year-old female who presents with abdominal pain [Auto-translated from Korean 배가 아파요]. for his symptoms 6 hours prior to presentation.
3. Rules for Content:
Synthesize, Don't List: Do not simply list the data points. Weave them into a coherent paragraph.
No New Information: You must not add any information or make assumptions that are not explicitly provided in the data below.
Use All Information: You must incorporate every piece of provided data into your narrative.
Avoid Redundancy: Each symptom or detail should be mentioned only once unless it adds new temporal or qualitative information. Do not repeat symptoms with only minor wording changes.
Preserve Logical Flow: Do not imply causal relationships between symptoms unless such relationships are explicitly stated. Avoid suggesting that one symptom caused or worsened another unless that is clearly indicated in the input.
Maintain Chronological and Thematic Clarity: Organize symptoms first chronologically, and secondarily by system (e.g., respiratory, gastrointestinal, genitourinary). If multiple symptoms begin on the same day, group them by system for readability.
Professional Language: Convert abbreviations (like M, F, CC, PMHx) and patient-friendly language into a formal clinical narrative. Use medically appropriate terminology and tone throughout.
Raw Clinical Data to Process:
${gender} / ${ageString}
CC
${cc}
===
Past Medical History:
${pmhData.join('\n')}
===
HPI:
${hpiData.join('\n')}
===
Medication:
${medicationData.join('\n')}
===
Allergy:
${allergyData.join('\n')}
===
Task:
Now, generate the HPI note based on the data and instructions provided.
    `.trim();

    const languageInstruction = isKorean
      ? 'Write the final output in Korean.'
      : 'Write the final output in English.';

    const safetyInstruction =
      'Do not add or assume any information that is not explicitly provided. Do not invent symptoms. Especially do not include hallucinations.';

    const prompt = `${englishPrompt}\n\n${languageInstruction}\n${safetyInstruction}`;

    // visitId를 여러 소스에서 가져옴 (useData의 visit 또는 useSurveyState의 visit)
    const visitId = visit?.uuid || visitFromSurveyState?.uuid || undefined;
    if (!visitId) {
      console.warn('[sendDescriptiveNoteToAthena] visitId not found - descriptive note may not be saved to CyrenCare');
    }
    await AthenaServices.handleHpiUpdate(encounterId, prompt, '[Patient-entered history above - Clinician review required]', visitId);
  };

  const handleNext = async () => {
    if (!onNext) {
      return;
    }
    setDirty(true);

    if (!isValid) {
      const firstKeyNotAnswered = first(qsKeysNotAnswered);
      setErrorQsKeys(qsKeysNotAnswered);

      if (firstKeyNotAnswered) {
        const el = document.getElementById(firstKeyNotAnswered);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        showAnswerAll();
      }
      return;
    }

    // Next 버튼 클릭 시 특정 question key의 값을 surveyState에 저장
    const { answerMap } = treeState || {};
    if (answerMap) {
      // Allergy 질문 체크 (QsInputMultiTextNested - 태그 기반)
      const allergyQuestionKey = 'q-pmhx-allergy-cause-nested-athena';
      const allergyQuestionKey2 = 'q-pmhx-allergy-cause-nested';
      
      // 모든 입력 필드를 찾고 questionKey와 일치하는 것을 찾기
      const allAllergyInputs = Array.from(document.querySelectorAll('input'));
      let allergyInput: HTMLInputElement | null = null;
      let actualAllergyKey: string | null = null;
      
      for (let i = 0; i < allAllergyInputs.length; i++) {
        const htmlInput = allAllergyInputs[i] as HTMLInputElement;
        const { id } = htmlInput;
        const dataQuestionKey = htmlInput.getAttribute('data-question-key');
        
        if ((id === allergyQuestionKey || dataQuestionKey === allergyQuestionKey) && 
            htmlInput.value && htmlInput.value.trim()) {
          allergyInput = htmlInput;
          actualAllergyKey = allergyQuestionKey;
          break;
        } else if ((id === allergyQuestionKey2 || dataQuestionKey === allergyQuestionKey2) && 
                   htmlInput.value && htmlInput.value.trim()) {
          allergyInput = htmlInput;
          actualAllergyKey = allergyQuestionKey2;
          break;
        }
      }
      
      // answerMap에서 실제 사용된 키 확인
      if (!actualAllergyKey) {
        if (answerMap[allergyQuestionKey]) {
          actualAllergyKey = allergyQuestionKey;
        } else if (answerMap[allergyQuestionKey2]) {
          actualAllergyKey = allergyQuestionKey2;
        }
      }
      
      // eslint-disable-next-line no-console
      
      if (actualAllergyKey && allergyInput && allergyInput.value && allergyInput.value.trim()) {
        // 입력 중인 값이 있으면 새로운 태그로 추가
        const inputValue = allergyInput.value.trim();
        let allergyValue = '';
        let currentAllergyAnswerMaps: any[] = [];
        
        // answerMap에서 기존 값 확인
        const allergyAnswer = answerMap[actualAllergyKey];
        if (allergyAnswer?.answerValues && allergyAnswer.answerValues.length > 0) {
          [allergyValue] = allergyAnswer.answerValues;
          currentAllergyAnswerMaps = allergyAnswer.answerMaps || [];
        }
        
        // 중복 체크 - 이미 존재하는 태그는 추가하지 않음
        const existingTagTexts = allergyValue ? allergyValue.split('|').filter(tag => tag.trim()) : [];
        
        if (!existingTagTexts.includes(inputValue)) {
          const newTagTexts = [...existingTagTexts, inputValue];
          allergyValue = newTagTexts.join('|');
          
          // 새로운 태그에 대한 answerMap 추가 (빈 배열로 시작)
          const newAnswerMaps = [
            ...currentAllergyAnswerMaps,
            {
              answerKeys: [],
              answerLabels: [],
              notesForKeys: {}
      }
          ];
          
          // eslint-disable-next-line no-console
          
          // updateAnswer 호출하여 새로운 태그 추가
          if (updateAnswer) {
            updateAnswer({
              questionKey: actualAllergyKey,
              answerValues: [allergyValue],
              answerMaps: newAnswerMaps.length > 0 ? newAnswerMaps : undefined
            });
            
            // 상태 업데이트가 반영되도록 여러 틱 동안 기다리기
            // React의 상태 업데이트는 비동기적이므로 여러 틱이 필요할 수 있습니다
            await new Promise<void>((resolve) => {
              setTimeout(() => {
                resolve();
              }, 0);
            });
            await new Promise<void>((resolve) => {
              setTimeout(() => {
                resolve();
              }, 0);
            });
            await new Promise<void>((resolve) => {
              setTimeout(() => {
                resolve();
              }, 0);
            });
          }
          
          // 입력 필드 초기화
          allergyInput.value = '';
          
          // surveyState 업데이트
        setSurveyState((prev: any) => ({
          ...prev,
          allergy: allergyValue.trim(),
        }));
          
          // allergyFinal 상태 업데이트 (모든 태그를 포함)
          // updateAnswer 호출 후 answerMap이 업데이트되었을 것이므로
          // 최신 answerMap을 사용하여 allergyFinal 구성
          const updatedAnswerMap = treeState?.answerMap || answerMap;
          const updatedAllergyAnswer = updatedAnswerMap[actualAllergyKey];
          
          if (updatedAllergyAnswer?.answerValues && updatedAllergyAnswer.answerValues.length > 0) {
            const [updatedAllergyValue] = updatedAllergyAnswer.answerValues;
            const updatedTagTexts = updatedAllergyValue ? updatedAllergyValue.split('|').filter(tag => tag.trim()) : [];
            const updatedAnswerMaps = updatedAllergyAnswer.answerMaps || [];
            
            // allergyFinal 구성 (QsInputMultiTextNested의 updateAllergyFinalState와 동일한 로직)
            const allergyFinal = updatedTagTexts.map((tagText, tagIndex) => {
              const tagAnswerMap = updatedAnswerMaps[tagIndex];
              const answerKeys = tagAnswerMap?.answerKeys || [];
              
              // answerKeys를 reactions로 변환 (nested-{questionIndex}-{optionIndex} 형식에서 key 추출)
              // 이 부분은 실제 nestedQuestions 구조가 필요하지만, 
              // 일단 answerKeys를 그대로 사용 (QsInputMultiTextNested에서 변환됨)
              const reactions = answerKeys; // 실제로는 nestedQuestions에서 key를 찾아야 함
              
              return {
                allergy: tagText,
                reactions
              };
            });
            
            setSurveyState((prev: any) => ({
              ...prev,
              allergyFinal
            }));
          }
        }
      }

      // Past Medication 질문 체크 (q-dhx-description-athena - QsInputMultiText - 태그 기반)
      const medicationQuestionKey = 'q-dhx-description-athena';
      const medicationQuestionKeyAlt = 'q-dhx-description'; // 실제 DOM ID는 이것일 수 있음
      
      // 모든 입력 필드를 찾고 questionKey와 일치하는 것을 찾기
      const allMedicationInputs = Array.from(document.querySelectorAll('input'));
      let medicationInput: HTMLInputElement | null = null;
      let actualMedicationKey: string = medicationQuestionKey;
      
      for (let i = 0; i < allMedicationInputs.length; i++) {
        const htmlInput = allMedicationInputs[i] as HTMLInputElement;
        const {id} = htmlInput;
        const dataQuestionKey = htmlInput.getAttribute('data-question-key');
        
        // ID 또는 data-question-key가 일치하고, 값이 있는 경우
        if ((id === medicationQuestionKey || id === medicationQuestionKeyAlt || 
             dataQuestionKey === medicationQuestionKey || dataQuestionKey === medicationQuestionKeyAlt) && 
            htmlInput.value && htmlInput.value.trim()) {
          medicationInput = htmlInput;
          // data-question-key가 있으면 그것을 사용, 없으면 ID 사용
          actualMedicationKey = dataQuestionKey || id || medicationQuestionKey;
          break;
        }
      }
      
      // answerMap에서 실제 키 확인 (answerMap에 저장된 키가 다를 수 있음)
      if (!medicationInput && answerMap[medicationQuestionKey]) {
        actualMedicationKey = medicationQuestionKey;
      } else if (!medicationInput && answerMap[medicationQuestionKeyAlt]) {
        actualMedicationKey = medicationQuestionKeyAlt;
      }
      
      // eslint-disable-next-line no-console
      
      if (medicationInput && medicationInput.value && medicationInput.value.trim()) {
        // 입력 중인 값이 있으면 새로운 태그로 추가
        const inputValue = medicationInput.value.trim();
        let medicationValue = '';
        
        // answerMap에서 기존 값 확인 (태그들) - 두 키 모두 확인
        const medicationAnswer = answerMap[actualMedicationKey] || 
                                 answerMap[medicationQuestionKey] || 
                                 answerMap[medicationQuestionKeyAlt];
        if (medicationAnswer?.answerValues && medicationAnswer.answerValues.length > 0) {
          [medicationValue] = medicationAnswer.answerValues;
        }
        
        // 중복 체크 - 이미 존재하는 태그는 추가하지 않음
        const existingTagTexts = medicationValue ? medicationValue.split('|').filter(tag => tag.trim()) : [];
        
        if (!existingTagTexts.includes(inputValue)) {
          const newTagTexts = [...existingTagTexts, inputValue];
          medicationValue = newTagTexts.join('|');
          
          // eslint-disable-next-line no-console
          
          // updateAnswer 호출하여 새로운 태그 추가
          if (updateAnswer) {
            updateAnswer({
              questionKey: actualMedicationKey,
              answerValues: [medicationValue]
            });
          }
          
          // 입력 필드 초기화
          medicationInput.value = '';
          
          // surveyState 업데이트
          setSurveyState((prev: any) => ({
            ...prev,
            currentMedication: medicationValue.trim(),
          }));
        }
      } else if (answerMap[medicationQuestionKey] || answerMap[medicationQuestionKeyAlt]) {
        // 입력 필드가 없지만 answerMap에 값이 있는 경우 surveyState 업데이트
        const medicationAnswer = answerMap[medicationQuestionKey] || answerMap[medicationQuestionKeyAlt];
        if (medicationAnswer?.answerValues && medicationAnswer.answerValues.length > 0) {
          const [medicationValue] = medicationAnswer.answerValues;
      if (typeof medicationValue === 'string' && medicationValue.trim()) {
        setSurveyState((prev: any) => ({
          ...prev,
          currentMedication: medicationValue.trim(),
        }));
          }
        }
      }

      // Medication Prior to Admission 질문 체크 (q-medication-pta-describe - QsTextInput)
      const medicationPtaKey = 'q-medication-pta-describe';
      let medicationPtaValue = '';
      
      // answerMap에서 값 확인
      const medicationPtaAnswer = answerMap[medicationPtaKey];
      if (medicationPtaAnswer?.answerValues && medicationPtaAnswer.answerValues.length > 0) {
        [medicationPtaValue] = medicationPtaAnswer.answerValues;
      }
      
      // 입력 중인 값도 확인 (DOM에서 직접 가져오기)
      const medicationPtaInput = document.querySelector(`#${medicationPtaKey}`) as HTMLInputElement;
      if (medicationPtaInput && medicationPtaInput.value && medicationPtaInput.value.trim()) {
        // 입력 중인 값이 있으면 기존 값과 합치기
        const inputValue = medicationPtaInput.value.trim();
        if (medicationPtaValue && medicationPtaValue.trim()) {
          medicationPtaValue = `${medicationPtaValue}|${inputValue}`;
        } else {
          medicationPtaValue = inputValue;
        }
      }
      
      if (typeof medicationPtaValue === 'string' && medicationPtaValue.trim()) {
        setSurveyState((prev: any) => ({
          ...prev,
          medicationCurrent: medicationPtaValue.trim(),
        }));
      }
    }

    // symptoms-more-associated으로 넘어가기 직전에 Athena 통합 실행
    const currIdx = survey?.questionTreeKeys?.findIndex((k) => k === treeKey) ?? -1;
    const nextTreeKey = currIdx !== -1 ? survey?.questionTreeKeys?.[currIdx + 1] : null;
    
    // eslint-disable-next-line no-console
    
    // allergy 페이지에서 etc로 넘어가기 직전에 Athena 통합 실행 (비동기로 실행, 응답 대기 안 함)
    const shouldRunMedicationIntegration = treeKey === 'stage-past-medical-history' && nextTreeKey === 'allergy';
    const shouldRunAthenaIntegration = treeKey === 'allergy' && nextTreeKey === 'pmhx-screen';
    
    if (shouldRunAthenaIntegration) {
      // eslint-disable-next-line no-console
      // 응답을 기다리지 않고 비동기로 실행
      (async () => {
        try {
          await sendDescriptiveNoteToAthena();
          await handleMedication();
          // eslint-disable-next-line no-console
        } catch (error) {
          console.error('[PaQuestionTree] Athena 통합 중 오류 발생:', error);
          // 오류가 발생해도 계속 진행 (이미 페이지는 넘어갔음)
        }
      })();
    }

    if (shouldRunMedicationIntegration) {
      // eslint-disable-next-line no-console
      // 응답을 기다리지 않고 비동기로 실행
      (async () => {
        try {
          // eslint-disable-next-line no-console
        } catch (error) {
          console.error('[PaQuestionTree] Athena 통합 중 오류 발생:', error);
          // 오류가 발생해도 계속 진행 (이미 페이지는 넘어갔음)
        }
      })();
    }

    try {
      onNext();
    }
    catch(error) {
      if (error instanceof ContentError) {
        const yes = await confirmDialog("", 
          sltStr({ key: 'msg-confirm-save' }),
          sltStr({ key: 'btn-cancel' }), 
          sltStr({ key: 'btn-submit' }));
        if (!yes) {
          return;
        }    
        if (onSave) onSave();
      }
      else {
        throw error;
      }
    }
  }

  return (
    <ErrorBoundary FallbackComponent={PaLocalStorageReset}>
      <div className='t_PaQuestionTree flex-1 flex-col gap-2'>
        {noHeader !== true && <PaTreeHeader survey={survey} tree={tree} />}
        <div className='px-5 flex-col gap-8'>
          {(treeLabel || desc) && showHeader && (
            <div
              className='flex-col gap-3'
              onClick={() => {
                // eslint-disable-next-line
              }}
            >
              {treeLabel && (
                <span className='flex-row justify-center'>
                  <div className={classNames('text-lg font-bold text-primary')}>{treeLabel}</div>
                </span>
              )}
              {desc && (
                <div className='flex-1 inline-block text-center'>
                  <div className='text-desc'>{desc}</div>
                </div>
              )}
            </div>
          )}
          {questions?.map((question) => {
            if (!question) return null;

            const { questionKey } = question;

            const isVisible = isQuestionVisible({ question, treeKey });

            // eslint-disable-next-line
            // isVisible

            if (!isVisible) {
              return null;
            }

            return (
              <Question
                level={level}
                dirty={dirty}
                error={errorQsKeys.includes(questionKey)}
                errorMsgs={qsErrorMap?.[question?.questionKey || '']}
                key={question?.questionKey}
                treeKey={treeKey}
                question={question}
                treeState={treeState}
                updateAnswer={wrappedUpdateAnswer}
                updateValue={updateValue}
                tOptions={extendedTOptions}
                onUiMetadataChange={onUiMetadataChange}
                locale={locale}
                mode={mode}
                onNext={handleNext}
                patientDetails={patientDetails}
              />
            );
          })}

          <div className='flex'>
            {confirmationDialogUX}
            {!hasNext && onSave && (
              <button
                className={classNames('btn btn-primary flex-1 btn-lg')}
                onClick={async () => {
                  setDirty(true);

                  if (!isValid) {
                    const firstKeyNotAnswered = first(qsKeysNotAnswered);
                    setErrorQsKeys(qsKeysNotAnswered);

                    if (firstKeyNotAnswered) {
                      const el = document.getElementById(firstKeyNotAnswered);
                      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      showAnswerAll();
                    }
                    return;
                  }

                  const yes = await confirmDialog("", 
                    sltStr({ key: 'msg-confirm-save' }),
                    sltStr({ key: 'btn-cancel' }), 
                    sltStr({ key: 'btn-submit' }));
                  if (!yes) {
                    return;
                  }

                  onSave();
                }}
              >
                {sltStr({ key: 'btn-save', capit: true })}
              </button>
            )}

            {hasNext && onNext && (
              <button
                className={classNames('btn btn-primary flex-1 btn-lg')}
                onClick={handleNext}
              >
                {sltStr({ key: 'btn-next', capit: true })}
              </button>
            )}
          </div>
          <div className='h-10' />
        </div>
      </div>
    </ErrorBoundary>
  );
}

export default memo(PaQuestionTree);
